// SPDX-License-Identifier: AGPL-3.0-or-later
//
// parley: what the soundboard panel remembers about *you* - favourites,
// how often you played which sound, which sections you folded away
// (strand AJ, docs/strands/soundboard.md).
//
// Where this lives was a deliberate choice. Fluxer's settings object is a
// strict allow-list on the server (internal/api/usersettings_validate.go
// drops unknown keys) and the conformance spec closes
// `UserSettingsResponse`, so a new top-level key would mean touching both.
// But the settings object already carries `synced_preferences`: a base64
// protobuf blob the API only measures, never parses. Our field lives in
// there, exactly like the emoji picker's favourites and frecency do, so
// favourites follow you to the next device and conformance stays at zero
// findings. The only fork change is our own proto file.
//
// The frecency maths is Fluxer's (features/emoji/state/UsageFrecency): a
// sound played twice today should outrank one played ten times in March,
// and that curve is already tuned and tested here.

import {
	bumpUsageEntry,
	dedupeBoundedIds,
	EMPTY_USAGE_RANKING,
	isValidUsageKey,
	MAX_TRACKED_USAGE_KEYS,
	mergeWireUsageMaps,
	rankUsageMap,
	sanitizeUsageMap,
	type UsageEntry,
	type UsageRanking,
	usageEntryFromWire,
	usageEntryToWire,
} from '@app/features/emoji/state/UsageFrecency';
import {makeSyncedField} from '@app/features/user/state/SyncedField';
import {SoundboardStateSchema} from '@fluxer/schema/src/gen/fluxer/user/preferences/v1/pickers_pb';
import {makeAutoObservable, untracked} from 'mobx';

const MAX_FAVORITE_SOUNDS = 200;
const MAX_COLLAPSED_SECTIONS = 100;
const MAX_RECENT_SOUNDS = 12;
const USAGE_SYNC_DEBOUNCE_MS = 1_500;

class SoundboardPreferences {
	/** sound id -> how often and how recently it was played by us. */
	usage: Record<string, UsageEntry> = {};
	favoriteSounds: Array<string> = [];
	collapsedSections: Array<string> = [];
	private favoriteSet = new Set<string>();
	private collapsedSet = new Set<string>();
	private ranking: UsageRanking = EMPTY_USAGE_RANKING;
	private rankingDirty = true;
	private rankingVersion = 0;

	constructor() {
		makeAutoObservable<
			SoundboardPreferences,
			'favoriteSet' | 'collapsedSet' | 'ranking' | 'rankingDirty' | 'rankingVersion'
		>(
			this,
			{favoriteSet: false, collapsedSet: false, ranking: false, rankingDirty: false, rankingVersion: false},
			{autoBind: true},
		);
		void this.initPersistence();
	}

	private async initPersistence(): Promise<void> {
		await makeSyncedField(this, {
			field: 'soundboard',
			schema: SoundboardStateSchema,
			persist: ['usage', 'favoriteSounds', 'collapsedSections'],
			debounceMs: USAGE_SYNC_DEBOUNCE_MS,
			toMessage: (state) => ({
				usage: Object.fromEntries(Object.entries(state.usage).map(([key, entry]) => [key, usageEntryToWire(entry)])),
				favoriteSoundIds: [...state.favoriteSounds],
				collapsedSectionIds: [...state.collapsedSections],
			}),
			applyMessage: (state, message) => {
				const now = Date.now();
				const usage: Record<string, UsageEntry> = {};
				for (const [key, stat] of Object.entries(message.usage)) {
					if (!isValidUsageKey(key)) continue;
					usage[key] = usageEntryFromWire(stat);
				}
				state.usage = sanitizeUsageMap(usage, now);
				state.favoriteSounds = dedupeBoundedIds(message.favoriteSoundIds, MAX_FAVORITE_SOUNDS);
				state.collapsedSections = dedupeBoundedIds(message.collapsedSectionIds, MAX_COLLAPSED_SECTIONS);
				state.favoriteSet = new Set(state.favoriteSounds);
				state.collapsedSet = new Set(state.collapsedSections);
				state.rankingDirty = true;
			},
			// Two devices that both played sounds should end up with the sum,
			// not with whichever wrote last; the id lists are small and a
			// plain take-theirs keeps the merge honest.
			mergeRemote: (local, incoming) => ({
				usage: mergeWireUsageMaps(local.usage, incoming.usage, Date.now()),
				favoriteSoundIds: [...incoming.favoriteSoundIds],
				collapsedSectionIds: [...incoming.collapsedSectionIds],
			}),
		});
		this.favoriteSet = new Set(this.favoriteSounds);
		this.collapsedSet = new Set(this.collapsedSections);
		this.rankingDirty = true;
	}

	private getRanking(): UsageRanking {
		if (this.rankingDirty) {
			this.rankingVersion += 1;
			this.ranking = untracked(() => rankUsageMap(this.usage, Date.now(), this.rankingVersion));
			this.rankingDirty = false;
		}
		return this.ranking;
	}

	isFavorite(soundId: string): boolean {
		void this.favoriteSounds.length;
		return this.favoriteSet.has(soundId);
	}

	toggleFavorite(soundId: string): void {
		if (this.favoriteSet.has(soundId)) {
			this.favoriteSet.delete(soundId);
			const index = this.favoriteSounds.indexOf(soundId);
			if (index > -1) this.favoriteSounds.splice(index, 1);
			return;
		}
		if (this.favoriteSounds.length >= MAX_FAVORITE_SOUNDS) return;
		this.favoriteSet.add(soundId);
		this.favoriteSounds.push(soundId);
	}

	isSectionCollapsed(sectionId: string): boolean {
		void this.collapsedSections.length;
		return this.collapsedSet.has(sectionId);
	}

	toggleSection(sectionId: string): void {
		if (this.collapsedSet.has(sectionId)) {
			this.collapsedSet.delete(sectionId);
			const index = this.collapsedSections.indexOf(sectionId);
			if (index > -1) this.collapsedSections.splice(index, 1);
			return;
		}
		if (this.collapsedSections.length >= MAX_COLLAPSED_SECTIONS) return;
		this.collapsedSet.add(sectionId);
		this.collapsedSections.push(sectionId);
	}

	/** Called when *we* press a sound; someone else's press is not ours. */
	trackPlay(soundId: string): void {
		if (!isValidUsageKey(soundId)) return;
		const now = Date.now();
		this.usage[soundId] = bumpUsageEntry(this.usage[soundId], now);
		if (Object.keys(this.usage).length > MAX_TRACKED_USAGE_KEYS) {
			this.usage = sanitizeUsageMap(this.usage, now);
		}
		this.rankingDirty = true;
	}

	/** Sound ids, best frecency first; the panel filters them to what exists. */
	recentSoundIds(limit: number = MAX_RECENT_SOUNDS): ReadonlyArray<string> {
		const ranked = this.getRanking().rankedKeys;
		return limit > 0 && ranked.length > limit ? ranked.slice(0, limit) : ranked;
	}
}

export default new SoundboardPreferences();
